import React, { useEffect, useState, useMemo, useCallback } from "react";
import { v4 as uuidv4 } from "uuid";
import { Base64 } from "js-base64";
import { useFormik } from "formik";
import { Alert } from "@material-ui/lab";
import Button from "@material-ui/core/Button";
import FormControl from "@material-ui/core/FormControl";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Grid from "@material-ui/core/Grid";
import Link from '@material-ui/core/Link';
import InputLabel from "@material-ui/core/InputLabel";
import { makeStyles } from "@material-ui/core/styles";
import Switch from "@material-ui/core/Switch";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import { SDCloudBackendAPIWithToken } from "apis/backendAPI";
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Slide from '@material-ui/core/Slide';
import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Paper from '@material-ui/core/Paper';
import { getDesignerObjects } from "../../../apis/APIServices";

// Local
import ProfileTable from "./ProfileTable";
import LoaderHoc from "components/HOC/LoaderHoc";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const inputProps = {
  disableAnimation: true,
  shrink: false,
};

const useStyles = makeStyles((theme) => ({
  gridContainer: {
    padding: "0 40px",
  },
  radioButton: {
    "& .MuiTypography-root": {
      marginLeft: "10px",
    },
  },
  formControl: {
    width: "100%",
    marginTop: "5px",
  },
  buttOutlined: {
    color: theme.palette.buttonColor.main,
    borderColor: theme.palette.buttonColor.main,
    "&:hover": {
      border: `1px solid ${theme.palette.buttonColor.main}`,
    },
  },
  paper: {
    display: "inline-block",
    marginTop: 8,
    padding: 4,
  }
}));

const CreateProfile = () => {
  const classes = useStyles();
  // states (store raw items, not JSX)
  const [menuItems, setMenuItems] = useState([]);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState("");
  const [notificationType, setNotificationType] = useState("");
  const [learningsRaw, setLearningsRaw] = useState([]);     
  const [showcasesRaw, setShowcasesRaw] = useState([]);      
  const [sandboxesRaw, setSandboxesRaw] = useState([]);      
  const [designerRaw, setDesignerRaw] = useState([]);        
  const [designerObjectsForDesigner, setDesignerObjectsForDesigner] = useState([]); 
  const [designerElements, setDesignerElements] = useState({});
  const [showLoader, setShowLoader] = useState(true);
  const [quotaErrors, setQuotaErrors] = useState({});
  const [hoursState, setHoursState] = useState({}); 
  const [dismissedAlerts, setDismissedAlerts] = useState({});
  const [open, setOpen] = useState(false);

  const columns = [
    { Header: "Template Name", accessor: "template_name" },
    { Header: "Name", accessor: "display_name" },
    { Header: "Permission", accessor: "permission" },
    { Header: "Lab Hours", accessor: "lab_hours" },
    { Header: "Allocated Hours", accessor: "allocate_hours" },
    { Header: "Max Templates", accessor: "max_allowed_templates" },
    { Header: "Allocate Quota", accessor: "quota" }
  ];

  useEffect(() => {
    const activeErrors = Object.keys(quotaErrors);
    setDismissedAlerts((prev) => {
      const updated = { ...prev };
      activeErrors.forEach((key) => {
        if (quotaErrors[key] && prev[key]) {
          updated[key] = false;
        }
      });
      return updated;
    });
  }, [quotaErrors]);

  const formik = useFormik({
    initialValues: {
      deal_id: "",
      profile_name: "",
      learningPermissions: [],
      learningViews: [],
      sandboxPermissions: [],
      sandboxViews: [],
      designerPermissions: [],
    },
    onSubmit: (values) => {
      // keep your existing submit behavior (reads DOM ids)
      let permissions = [];
      let max_allowed_templates = 0
      let quotaValidationErrors = {};
      values.learningViews.forEach((item) => {
        let payload = {
          resource_type: "Learning",
          resource_name: item,
          permission: "view",
          alloc_hours: 0,
        };
        if (!values.learningPermissions.includes(item)) {
          permissions.push(payload);
        }
      });

      values.learningPermissions.forEach((item) => {
        const quotaValue = Number(document.getElementById(`${item}_quota`).value || 0);
        const permissionType = "create";
        if (permissionType === "create" && quotaValue <= 0) {
          quotaValidationErrors[item] = `Quota is required and must be greater than 0 for ${item}`
        }

        let payload = {
          resource_type: "Learning",
          resource_name: item,
          permission: permissionType,
          alloc_hours: item.allocated_hours || 0,
          quota_value: document.getElementById(`${item}_quota`).value || 0
        };
        permissions.push(payload);
      });

      values.sandboxViews.forEach((item) => {
        let payload = {
          resource_type: "Sandbox",
          resource_name: item,
          permission: "view",
          alloc_hours: item.allocated_hours || 0,
          quota_value: document.getElementById(`${item}_quota`).value || 0
        };
        if (!values.sandboxPermissions.includes(item)) {
          permissions.push(payload);
        }
      });

      values.sandboxPermissions.forEach((item) => {
        const quotaValue = Number(document.getElementById(`${item}_quota`).value || 0);
        const permissionType = "create";
        if (permissionType === "create" && quotaValue <= 0) {
          quotaValidationErrors[item] = `Quota is required and must be greater than 0 for ${item}`
        }
        let payload = {
          resource_type: "Sandbox",
          resource_name: item,
          permission: permissionType,
          alloc_hours: item.allocated_hours || 0,
          quota_value: document.getElementById(`${item}_quota`).value || 0
        };

        if (item === "designer_portal") {
          payload["designer_objects"] = values.designerPermissions;
          max_allowed_templates = document.getElementById(`${item}_max_allowed_templates`).value || 0
        }

        permissions.push(payload);
      });

      if (Object.keys(quotaValidationErrors).length > 0) {
        setQuotaErrors(quotaValidationErrors);
        return;
      }

      let payload = {
        profile_name: `${values.deal_id}_${values.profile_name}`,
        permissions: permissions,
        max_allowed_templates: max_allowed_templates
      };

      SDCloudBackendAPIWithToken()
        .post("/create_profile", payload)
        .then((resp) => resp.data)
        .then((data) => {
          setShowNotification(true);
          setNotificationType("success");
          setNotificationMessage("Profile created successfully");
        })
        .catch((err) => {
          let message = err?.response?.data?.message || "Something went wrong";
          setShowNotification(true);
          setNotificationType("error");
          setNotificationMessage(message);
        });
    },
    validate: (values) => {
      let errors = {};
      if (!values.deal_id) { errors.deal_id = "Required"; }
      if (!values.profile_name) { errors.profile_name = "Required"; }
      return errors;
    },
  });

  const checkQuotaRange = useCallback((value, resourceName, currentAllocated, lab_hours) => {
    const quota = Number(value);
    let error = "";
    if (!value && value !== 0 && value !== "0") {
      error = "";
    } else if (value && value.includes(".")) {
      error = `Decimal values are not allowed for ${resourceName}. Please enter a whole number.`;
    } else if (!isNaN(quota) && quota > Number(currentAllocated)) {
      error = `Allocate Quota for ${resourceName} cannot exceed allocated hours ${currentAllocated}.`;
    } else if (lab_hours) {
      const min = Number(lab_hours);
      const max = 3 * Number(lab_hours);
      if (quota < min) {
        error = `Allocate Quota for ${resourceName} should be at least equal to lab hours for Learnings and Showcases`;
      } else if (quota > max) {
        error = `Allocate Quota for ${resourceName} cannot exceed 3 times of lab hours for Learnings and Showcases`;
      }
    } else {
      if (quota <= 0) {
        error = `Allocate Quota for ${resourceName} cannot be less than or equal to 0 for Sandboxes and Design Center`;
      } else if (quota > 20) {
        error = `Allocate Quota for ${resourceName} cannot exceed 20 for Sandboxs and Design Center`;
      }
    }

    setQuotaErrors(prev => {
      const newErrors = { ...prev };
      if (error) newErrors[resourceName] = error;
      else delete newErrors[resourceName];
      return newErrors;
    });
  }, []);

  // stable handler for quota input
  const handleQuotaChange = useCallback((resourceName, displayName, allocatedHours, labHours) => (e) => {
    const value = e.target.value;
    const newQuota = value === "" ? "" : Number(value);
    setHoursState(prev => ({ ...prev, [resourceName]: newQuota }));
    if (!isNaN(newQuota) && newQuota > 0) {
      setQuotaErrors(prev => {
        const p = { ...prev };
        delete p[resourceName];
        return p;
      });
    }

    checkQuotaRange(value, displayName, allocatedHours, labHours);
  }, [checkQuotaRange]);

  // renderTemplateData now stores raw items (no JSX)
  const renderTemplateData = async () => {
    try {
      const resp = await SDCloudBackendAPIWithToken().get("/get_resources");
      const data = resp.data;
      const learnings = [];
      const showcases = [];
      const sandboxes = [];
      const designer = [];
      let designerObjects = [];

      data.learnings.forEach(item => {
        const raw = {
          name: item.name,
          display_name: item.display_name,
          labhours: item.labhours,
          allocated_hours: item.allocated_hours || 0,
          is_scripted_demo: item.is_scripted_demo ?? false
        };
        if (raw.is_scripted_demo) showcases.push(raw);
        else learnings.push(raw);
      });

      data.sandboxes.forEach(item => {
        const raw = {
          name: item.name,
          display_name: item.display_name,
          labhours: item.labhours,
          allocated_hours: item.allocated_hours || 0,
          designer_objects: item.designer_objects || []
        };

        if (item.name === "designer_portal") {
          designerObjects = raw.designer_objects;
          designer.push(raw);
        } else {
          sandboxes.push(raw);
        }
      });

      setLearningsRaw(learnings);
      setShowcasesRaw(showcases);
      setSandboxesRaw(sandboxes);
      setDesignerRaw(designer);
      setDesignerObjectsForDesigner(designerObjects);
      setShowLoader(false);
    } catch (err) {
      console.error("Failed to fetch resources", err);
      setShowLoader(false);
    }
  };

  const renderDealIdDropdown = async () => {
    try {
      let accountName = JSON.parse(localStorage.getItem("profileData"))?.account;
      const resp = await SDCloudBackendAPIWithToken().get(`/get_engagement_ids?account=${accountName}`);
      const data = resp.data;
      let engagements = data.engagements_id_list || [];
      let items = engagements.map((item) => (<option key={uuidv4()} value={item}>{item}</option>));
      items = [<option key={uuidv4()} value={null} selected>None</option>, ...items];
      setMenuItems(items);
    } catch (err) {
      console.error(err);
    }
  };

  const getDesignObjects = async () => {
    const designObject = localStorage.getItem("design_objects");
    if (!designObject) {
      const response = await getDesignerObjects();
      if (response.success) {
        localStorage.setItem("design_objects", JSON.stringify(response.data));
      }
    }
  };

  useEffect(() => {
    renderDealIdDropdown();
    getDesignObjects();
    renderTemplateData();
  }, []);
  // Build rows (JSX) from raw arrays so they react to hoursState changes
  const learningsRows = useMemo(() => {
    return learningsRaw.map((item) => ({
      template_name: item.name,
      display_name: item.display_name,
      permission: (
        <FormControlLabel control={<Switch value={item.name} name="learningPermissions" color="default" onChange={formik.handleChange} />} />
      ),
      lab_hours: item.labhours,
      allocate_hours: item.allocated_hours || 0,
      quota: (
        <TextField
          type="number"
          variant="filled"
          name={`${item.name}_quota`}
          fullWidth
          style={{ paddingRight: "15px" }}
          onChange={handleQuotaChange(item.name, item.display_name, item.allocated_hours, item.labhours)}
          inputProps={{ id: `${item.name}_quota` }}
          InputLabelProps={inputProps}
          value={hoursState[item.name] ?? ""}
        />
      )
    }));
  }, [learningsRaw, hoursState, formik, handleQuotaChange]);

  const showcasesRows = useMemo(() => {
    return showcasesRaw.map((item) => ({
      template_name: item.name,
      display_name: item.display_name,
      permission: (
        <FormControlLabel control={<Switch value={item.name} name="learningPermissions" color="default" onChange={formik.handleChange} />} />
      ),
      lab_hours: item.labhours,
      allocate_hours: item.allocated_hours || 0,
      quota: (
        <TextField
          type="number"
          variant="filled"
          name={`${item.name}_quota`}
          fullWidth
          style={{ paddingRight: "15px" }}
          onChange={handleQuotaChange(item.name, item.display_name, item.allocated_hours, item.labhours)}
          inputProps={{ id: `${item.name}_quota` }}
          InputLabelProps={inputProps}
          value={hoursState[item.name] ?? ""}
        />
      )
    }));
  }, [showcasesRaw, hoursState, formik, handleQuotaChange]);

  const sandboxesRows = useMemo(() => {
    return sandboxesRaw.map((item) => ({
      template_name: item.name,
      display_name: item.display_name,
      permission: (
        <FormControlLabel control={<Switch value={item.name} name="sandboxPermissions" color="default" onChange={formik.handleChange} />} />
      ),
      lab_hours: item.labhours,
      allocate_hours: item.allocated_hours || 0,
      quota: (
        <TextField
          type="number"
          variant="filled"
          name={`${item.name}_quota`}
          fullWidth
          style={{ paddingRight: "15px" }}
          onChange={handleQuotaChange(item.name, item.display_name, item.allocated_hours)}
          inputProps={{ id: `${item.name}_quota` }}
          InputLabelProps={inputProps}
          value={hoursState[item.name] ?? ""}
        />
      )
    }));
  }, [sandboxesRaw, hoursState, formik, handleQuotaChange]);

  const designerRows = useMemo(() => {
    // designerRaw likely contains only designer_portal item
    return designerRaw.map((item) => ({
      template_name: item.name,
      display_name: "Designer",
      permission: (
        <FormControlLabel control={<Switch value={item.name} name="sandboxPermissions" color="default" onChange={formik.handleChange} />} />
      ),
      lab_hours: item.labhours,
      allocate_hours: item.allocated_hours || 0,
      max_allowed_templates: (
        <TextField
          type="number"
          variant="filled"
          name={`${item.name}_max_allowed_templates`}
          fullWidth
          InputProps={{ id: `${item.name}_max_allowed_templates` }}
          InputLabelProps={inputProps}
          defaultValue={0}
          style={{ paddingRight: "15px" }}
        />
      ),
      quota: (
        <TextField
          type="number"
          variant="filled"
          name={`${item.name}_quota`}
          fullWidth
          style={{ paddingRight: "15px" }}
          onChange={handleQuotaChange(item.name, item.display_name, item.allocated_hours)}
          inputProps={{ id: `${item.name}_quota` }}
          InputLabelProps={inputProps}
          value={hoursState[item.name] ?? ""}
        />
      )
    }));
  }, [designerRaw, hoursState, formik, handleQuotaChange]);

  // dialog for designer elements
  const handleClickOpen = () => {
    try {
      var design_objects = JSON.parse(localStorage.getItem("design_objects"));
      if (!design_objects) return;
      var xml_objects = JSON.parse(Base64.decode(design_objects.xml))
      var name_icon_obj_map = {};
      for (let key in xml_objects) {
        if (!xml_objects.hasOwnProperty(key)) continue;
        var value = xml_objects[key];
        if (value['title'] == "Deprecated Devices" || value['title'].includes("Shapes")) continue;
        var shape_obj_list = []
        let stencilXMl = value['stencilXML']
        let xmlString = Base64.decode(stencilXMl)
        var parser = new DOMParser();
        var xmlDoc = parser.parseFromString(xmlString,"text/xml");
        let innxml = xmlDoc.getElementsByTagName('shape');
        for (var i = 0; i < innxml.length; i++) {
          var shape_obj_map = {}
          var shapexml = innxml[i];
          var shapename = shapexml.getAttribute('name')
          var childnodes = shapexml.childNodes
          var iconurl;
          for (var j = 0; j < childnodes.length; j++){
            if (childnodes[j].nodeName == 'background'){
              for(var k = 0; k < childnodes[j].childNodes.length; k++){
                if(childnodes[j].childNodes[k].nodeName == 'path'){
                  for(var l = 0; l < childnodes[j].childNodes[k].childNodes.length; l++){
                    if( childnodes[j].childNodes[k].childNodes[l].nodeName == 'image'){
                      iconurl = childnodes[j].childNodes[k].childNodes[l].getAttribute('src')
                    }
                  }
                }
              }
            }
          }
          if(iconurl != undefined){
            if(iconurl.includes('http')){
              shape_obj_map[shapename] = iconurl
            } else {
              shape_obj_map[shapename] = window.location.origin + '/' + 'design_center/' + iconurl
            }
          }
          shape_obj_list.push(shape_obj_map)
        }
        name_icon_obj_map[value['title']] = shape_obj_list
      }
      setDesignerElements(name_icon_obj_map)
      setOpen(true);
    } catch (err) {
      console.error("designer xml parse error", err);
    }
  };

  const handleClose = () => { setOpen(false); };

  return (
    <div>
      <form onSubmit={formik.handleSubmit}>
        <Grid container className={classes.gridContainer}>
          <Grid item md={12}>
            <Typography variant="h4">Create new profile</Typography>
          </Grid>

          <Grid item md={12}>
            <Grid container justify="space-between" alignItems="flex-end">
              <Grid item xs={12} md={5}>
                <FormControl variant="filled" className={classes.formControl}>
                  <InputLabel>Select Deal ID</InputLabel>
                  <select
                    name="deal_id"
                    value={formik.values.deal_id}
                    onChange={formik.handleChange}
                    style={{ width: "100%", height: 40 }}
                  >
                    {menuItems.length > 0 ? menuItems : <option value="">None</option>}
                  </select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={5}>
                {formik.errors.profile_name && formik.touched.profile_name ? (
                  <TextField
                    type="text"
                    variant="filled"
                    label="Profile Name"
                    name="profile_name"
                    fullWidth
                    InputLabelProps={inputProps}
                    value={formik.values.profile_name}
                    onChange={formik.handleChange}
                    helperText={formik.errors.profile_name}
                    onBlur={formik.handleBlur}
                    error
                  />
                ) : (
                  <TextField
                    type="text"
                    variant="filled"
                    label="Profile Name"
                    name="profile_name"
                    fullWidth
                    InputLabelProps={inputProps}
                    value={formik.values.profile_name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                )}
              </Grid>
            </Grid>
          </Grid>

          {showLoader ? (
            <div style={{ width: "100%", display: "flex", justifyContent: "center", marginTop: "80px" }}>
              <LoaderHoc isLoading={showLoader} />
            </div>
          ) : (
            <>
              <Grid item xs={12} md={12} style={{ marginTop: "35px" }}>
                <ProfileTable
                  columns={columns}
                  data={learningsRows}
                  tableTitle="Learn"
                  buttonTitle="Create Profile"
                  dropDownValues={[]}
                  hiddenColumns={["template_name", "max_allowed_templates"]}
                />
              </Grid>

              <Grid item xs={12} md={12} style={{ marginTop: "35px" }}>
                <ProfileTable
                  columns={columns}
                  data={showcasesRows}
                  tableTitle="Showcase"
                  buttonTitle="Create Profile"
                  dropDownValues={[]}
                  hiddenColumns={["template_name", "max_allowed_templates"]}
                />
              </Grid>

              <Grid item xs={12} md={12} style={{ marginTop: "35px" }}>
                <ProfileTable
                  columns={columns}
                  data={sandboxesRows}
                  tableTitle="Sandbox"
                  buttonTitle="Create Profile"
                  dropDownValues={[]}
                  hiddenColumns={["template_name", "lab_hours", "max_allowed_templates"]}
                />
              </Grid>

              <Grid item xs={12} md={12} style={{ marginTop: "35px" }}>
                <ProfileTable
                  columns={columns}
                  data={designerRows}
                  tableTitle="Designer"
                  buttonTitle="Create Profile"
                  dropDownValues={[]}
                  hiddenColumns={["template_name", "lab_hours"]}
                />
                <Grid item xs={12} md={6} style={{ marginTop: "20px" }}>
                  <Typography>
                    <Link href="#" onClick={handleClickOpen}>Show Design Center Elements</Link>
                  </Typography>
                </Grid>
              </Grid>

              <Grid item xs={12} md={12}>
                {Object.entries(quotaErrors).map(([resource, message]) => {
                  if (!message || dismissedAlerts[resource]) return null;
                  return (
                    <Alert key={resource} severity="error" onClose={() => setDismissedAlerts(prev => ({ ...prev, [resource]: true }))} sx={{ mb: 1 }}>
                      {message}
                    </Alert>
                  );
                })}
              </Grid>

              <Grid item xs={12} md={12}>
                {showNotification ? (
                  <Alert onClose={() => setShowNotification(false)} severity={notificationType}>
                    {notificationMessage}
                  </Alert>
                ) : null}
              </Grid>

              <Grid item xs={12} md={2}>
                <Button variant="outlined" color="secondary" disableElevation size="large" fullWidth className={classes.buttOutlined} type="submit" disabled={Object.values(quotaErrors).some(Boolean)}>
                  Submit
                </Button>
              </Grid>
            </>
          )}
        </Grid>
      </form>

      <Dialog open={open} TransitionComponent={Transition} keepMounted onClose={handleClose}>
        <DialogTitle><b>Design Centre Elements</b></DialogTitle>
        <DialogContent>
          {Object.keys(designerElements).map((key) => (
            <Accordion key={key}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>{key}</Typography>
              </AccordionSummary>
              <Grid container spacing={4}>
                {Object.entries(designerElements[key]).map(([, value], idx) => (
                  <div key={idx}>
                    {Object.entries(value).map(([iconkey, iconurl]) => (
                      <AccordionDetails key={iconkey}>
                        <Grid item xs={5}>
                          <img src={iconurl} height='56' width={56} alt={iconkey}/>
                          <Paper className={classes.paper}>{iconkey}</Paper>
                        </Grid>
                      </AccordionDetails>
                    ))}
                  </div>
                ))}
              </Grid>
            </Accordion>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">Close</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default CreateProfile;
